import React from 'react';

import { Text, View } from '@react-pdf/renderer';

import { extractBlockAlignment, splitMarkdownIntoBlocks } from './printMarkdown';

// Single-style-object variant of View/Text's `style` prop (excludes the array/undefined
// branches) — every style passed into this module is always one concrete style object.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PdfStyle = Exclude<React.ComponentProps<typeof View>['style'], any[] | undefined>;

interface MarkdownPdfStyles {
  paragraph: PdfStyle;
  heading: PdfStyle;
  listItem: PdfStyle;
  bold: PdfStyle;
  italic: PdfStyle;
  divider: PdfStyle;
}

// Splits on **bold**/*italic* spans. Runs on the whole block (not per line) so a span can cross
// embedded newlines — otherwise a multi-line ${nextScheduleDate} wrapped in **...** would leave
// literal "**" on its first/last line.
const renderInline = (
  text: string,
  styles: Pick<MarkdownPdfStyles, 'bold' | 'italic'>,
  keyPrefix: string
): React.ReactNode[] =>
  text
    .split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
    .filter(Boolean)
    .map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <Text key={`${keyPrefix}-${idx}`} style={styles.bold}>
            {part.slice(2, -2)}
          </Text>
        );
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return (
          <Text key={`${keyPrefix}-${idx}`} style={styles.italic}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      return part;
    });

// Renders a paragraph block with its line breaks preserved (react-pdf's <Text> honors literal
// "\n" characters in its children as forced line breaks), rather than collapsing every line
// into a single space — so a single Enter in the org's authored text shows as a visible break
// here too, matching the Preview tab. `alignStyle` (from the block's `[align:X]` marker, if any)
// matches what the Preview tab shows, so downloaded PDFs stay consistent.
const renderParagraph = (
  block: string,
  styles: MarkdownPdfStyles,
  key: number,
  alignStyle?: PdfStyle
) => (
  <Text key={key} style={alignStyle ? [styles.paragraph, alignStyle] : styles.paragraph}>
    {renderInline(block, styles, `${key}`)}
  </Text>
);

// Minimal markdown -> @react-pdf/renderer element mapper covering what the Preview tab renders:
// paragraphs, #/##/### headings, **bold**/*italic* spans, -/* bullet lists, a --- divider, and
// paragraph/heading alignment (see printMarkdown.ts's `[align:X]` marker).
export const renderMarkdownToPdf = (
  markdown: string,
  styles: MarkdownPdfStyles
): React.ReactNode[] => {
  const blocks = splitMarkdownIntoBlocks(markdown);

  return blocks.map((rawBlock, idx) => {
    if (rawBlock === '---') {
      return <View key={idx} style={styles.divider} />;
    }

    const { align, text: block } = extractBlockAlignment(rawBlock);

    const alignStyle: PdfStyle | undefined =
      align !== 'left' ? ({ textAlign: align } as PdfStyle) : undefined;

    const headingMatch = block.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const headingStyle = alignStyle
        ? [styles.paragraph, styles.heading, alignStyle]
        : [styles.paragraph, styles.heading];
      return (
        <Text key={idx} style={headingStyle}>
          {renderInline(headingMatch[2], styles, `${idx}-heading`)}
        </Text>
      );
    }

    const lines = block.split('\n').map((line) => line.trim());
    const isList = lines.every((line) => /^[-*]\s+/.test(line));
    if (isList) {
      return (
        <View key={idx} style={styles.paragraph}>
          {lines.map((line, lineIdx) => (
            <Text key={lineIdx} style={styles.listItem}>
              {'• '}
              {renderInline(line.replace(/^[-*]\s+/, ''), styles, `${idx}-${lineIdx}`)}
            </Text>
          ))}
        </View>
      );
    }

    return renderParagraph(block, styles, idx, alignStyle);
  });
};
